import React, { useId } from 'react';
import { CharacterBlockGroup, FieldCoordinates, FieldTextAlign } from '../types/codeGeneration';
import { isValidFieldName, findFieldNameCollision, pdfFieldName } from '../utils/fieldValidation';
import {
  CHARACTER_BLOCK_GROUP_PRESETS,
  characterBlockBoxCount,
  characterBlockGroupWarnings,
  characterBlockNamedCount,
  gapLayoutForGroups,
  resolveCharacterBlockGroups,
} from '../utils/characterBlockGroups';
import { autoFontSize } from '../utils/fieldFonts';

// Character block defaults — mirrored from PDFViewer's field-creation constants
const DEFAULT_BLOCK_WIDTH = 25;
/** Floor for a preset-derived block width; matches the manual input's own minimum. */
const MIN_BLOCK_WIDTH = 10;
const DEFAULT_BLOCK_SPACING = 5;
const DEFAULT_TOTAL_BLOCKS = 9;
// There is deliberately no DEFAULT_GROUP_PATTERN. The division is `groups`, and the server
// derives the "3-2-4" string from it; writing a default here is how a nine-box block ended up
// carrying groups:[{boxes:9}] and groupPattern:'3-2-4' at the same time.
const DEFAULT_GROUP_SEPARATOR = '-';
const DEFAULT_MAX_CHARS_PER_BLOCK = 1;
const DEFAULT_VALIDATION_PATTERN = '[0-9]';

/** DOM id of the name input — PDFViewer focuses it right after drawing a new field. */
export const FIELD_PROPS_NAME_INPUT_ID = 'pfb-field-props-name';
export const FIELD_PROPS_NAME_ERROR_ID = 'pfb-field-props-name-error';

/* ---------------------------------------------------------------------------
 * Accessibility guidance shown at the point of use.
 *
 * Wording is approved and lives in docs/design/PDFUA_FIELD_PROPERTY_UI_SPEC.md
 * (MICROCOPY section) — do not paraphrase it here. The audience is a municipal
 * clerk, not a developer, so none of these sentences says PDF/UA, WCAG, "tag"
 * or "structure tree"; each says what happens and what to do instead.
 *
 * Each sentence is used TWICE — as the control's title tooltip and as the
 * inline paragraph beneath it — which is why it is a constant rather than
 * inline JSX. Curly quotes and em dashes are literal characters, not HTML
 * entities, because these strings also render into a title attribute.
 * ------------------------------------------------------------------------- */

/** MICROCOPY 1 — Field Label, the accessible name. */
const GUIDANCE_FIELD_LABEL =
  'Screen readers announce THIS text, not the label printed on your PDF. The printed label is part of the page image and is never read aloud. Type it exactly as it appears on the form.';

/** MICROCOPY 1 — the inline prompt while the label is empty or still the machine name. */
const GUIDANCE_FIELD_LABEL_PROMPT =
  'Needed for screen readers. The label printed on your PDF is an image to assistive technology — type it here, exactly as it appears.';

/** MICROCOPY 4 — added to the Field Label guidance when the field is a radio group,
 *  because for a group the label is the QUESTION the options answer, not a heading. */
const GUIDANCE_RADIO_GROUP_LABEL =
  'For a group of choices, this is the question, not a heading. A screen reader reads this name and then the options — so “How would you like to be contacted?” works, while “Contact” leaves the listener guessing what the choices mean.';

/** MICROCOPY 2 — Format Hint. */
const GUIDANCE_FORMAT_HINT =
  'Someone using a screen reader cannot see the example printed beside the box. Whatever you type here is read out with the field, so write the format plainly — “MM/DD/YYYY”, or “10 digits, no spaces”.';

/** MICROCOPY 3 — Required. */
/** MICROCOPY — the per-option label on a radio group.
 *
 *  Scott, 2026-09-16: *"the user creating the fields is placing the dimensions of an actual
 *  radio button box over the physical location of the selectable radio button area"* — so the
 *  option's words are already printed on the source PDF and PFB must not draw them again.
 *
 *  This property is therefore the ACCESSIBLE name, not a caption to render.
 *
 *  WHERE IT GOES, and it is built — D23. `/Opt` IS the in-spec home: ISO 32000-1 Table 227
 *  defines it as one text string per kid, "representing the on state of the corresponding
 *  widget annotation", in `/Kids` order. A per-kid `/TU` is forbidden outright by clause
 *  12.7.3.2, so this is the only slot there is, and what the author types here is now what
 *  lands in it — on both generation paths.
 *
 *  The earlier note here said the slot did not exist, on the strength of a measured
 *  `setValue` rejection. The rejection was real and the conclusion was not: PDFBox validates
 *  `setValue`'s argument against `/Opt`, so once `/Opt` holds labels the LABEL is the
 *  argument. The first attempt changed `/Opt` and kept passing the export value. */
const GUIDANCE_RADIO_OPTION_LABEL =
  'The words for this option are already printed on your PDF — this box adds nothing to the page. It is what a screen reader announces when someone lands on this button, so match the printed text: “Express”, not “Option 2”. Left blank, a listener hears the question and has no way to tell the choices apart.';

const GUIDANCE_REQUIRED =
  'A red asterisk is invisible to a screen reader. Ticking Required is what actually announces this field as mandatory, and what stops the form being submitted without it.';

/** MICROCOPY 4b — the same control on a CHECKBOX, where "required" means something
 *  categorically different and the generic wording actively misleads.
 *
 *  Scott, 2026-09-13: "leaving the checkbox blank to match its, most likely, original
 *  state is a valid choice which happened to be the correct state."
 *
 *  He is right, and the reason is sharper than "it is unusual": A SINGLE CHECKBOX CANNOT
 *  DISTINGUISH "ANSWERED NO" FROM "DID NOT ANSWER" — both are the unticked state. So the
 *  thing Required is meant to guarantee, that the user made a deliberate choice, is
 *  literally inexpressible with one checkbox. Enforcing it does not ensure a choice; it
 *  refuses one of the two answers.
 *
 *  The one case where it IS coherent is the attestation — "I certify", "I accept the
 *  terms" — where unticked does not mean *no*, it means *cannot proceed*. That is the case
 *  HTML's required attribute on a checkbox exists for, and the one carrying legal weight
 *  for a government buyer. So the control stays and the WORDING narrows to that meaning. */
const GUIDANCE_REQUIRED_CHECKBOX =
  'Only for an attestation — “I certify this is accurate”, “I accept the terms” — where leaving it unticked means the form cannot be submitted at all. If you are asking a yes/no question, use a Radio Group instead: an unticked box cannot be told apart from an unanswered one, so “no” and “not answered” look identical.';

/** MICROCOPY 4c — Read-only, the sibling of Required and the one control that can carry a
 *  value the reader must see and must not change.
 *
 *  THE THING TO SAY, AND THE ONE AUTHORS GET WRONG: read-only is NOT hidden. `/Ff` bit 1
 *  exempts nothing — the field keeps its /TU, keeps its Form parent and its /StructParent,
 *  stays in the reading order and is announced WITH its value
 *  (`PDFUA_NORMATIVE_REFERENCE.md:1467`). Hiding the same value would take it away from the
 *  one person who cannot see the page, which is why PFB has no hidden field at all. */
const GUIDANCE_READ_ONLY =
  'For a value the reader must see but must not change — a case number, a computed total, an agency reference already filled in. A screen reader still announces the field and reads its value out; it simply cannot be typed in. This is not the same as hiding the value: a hidden value is gone for the one person who cannot see the page.';

/** MICROCOPY 5 — the character-block count. */
const GUIDANCE_CHARACTER_BLOCK =
  'The boxes tell a sighted person how many characters to enter. Nothing tells someone using a screen reader, so the count is added to this field’s name automatically. Name the field plainly — “Area code” — and it will be announced as “Area code, 3 characters, one per box”.';

/** MICROCOPY 6 — Character Block groups. Replaces the sentence above once a block
 *  is split, because from that point the count is no longer the whole story: each
 *  group becomes its own field and needs its own name. */
const GUIDANCE_CHARACTER_BLOCK_GROUPS =
  'Each group of boxes becomes its own field, so each needs its own name. A screen reader announces the field name and then the group — “Phone number, area code” — so name the groups by what they mean, not where they sit.';

/** MICROCOPY 7 — the Signature Line, whose label is its ONLY name. */
const GUIDANCE_SIGNATURE_LINE =
  'A line the person signs in ink after printing — not a box to type in. Draw it where the printed line goes; the rule is drawn along the bottom edge. The label is the only thing a screen reader has to go on, so write what the line is for: “Signature of applicant”, not “Signature”.';

/** MICROCOPY 7b — why a text field over a signature line is the thing to stop doing. */
const GUIDANCE_SIGNATURE_LINE_WHY =
  'Drawing a text field here instead makes a screen reader announce “edit box” on something that can only be signed with a pen.';

/** MICROCOPY 8 — the Push Button, the one control whose name does not come from /TU. */
const GUIDANCE_PUSH_BUTTON =
  'The label is the words printed ON the button and the name a screen reader announces — for a button those are the same entry, and it is the only control in PDF where that is true. Write the action: “Clear form”, not “Button 1”.';

/** MICROCOPY 8b — the action. */
const GUIDANCE_PUSH_BUTTON_ACTION =
  'Clear form empties every field. Open a link sends the reader to a web page. There is no “submit”: a PDF that posts your answers somewhere is the action this product strips out of every file it is given, and turning it on is a decision for the form’s owner.';

/* ---------------------------------------------------------------------------
 * MICROCOPY 9 — the image region. Wording is from
 * docs/design/IMAGE_REGION_AUTHORING.md sections 1-3 and is approved there; do
 * not paraphrase it here.
 *
 * The author is never shown the words "Alt", "ActualText" or "artifact". Those
 * are format keys and the author does not owe us knowledge of them — the
 * question is about the PICTURE. Getting the first two branches the wrong way
 * round is Matterhorn 13-005 / 13-008, both Human-judged, so no validator will
 * ever catch it and this copy is the only control there is.
 * ------------------------------------------------------------------------- */

/** The control itself: what it is, and what it is NOT. */
const GUIDANCE_IMAGE_REGION =
  'Draw this over a picture that is already on your PDF — a seal, a logo, a photo, a chart. It adds no image and changes nothing you can see; it tells a screen reader what the picture is. Nothing here is typed into by the person filling in the form.';

/** The three-way question. Deliberately about the image, never about a format key. */
const GUIDANCE_IMAGE_PURPOSE =
  'What does this image do? A picture that means something needs describing. A picture that is really words — a logo spelling a name — needs those words instead. A line or a flourish needs nothing at all, and saying so is the right answer, not a cop-out.';

/** Branch 1, the part authors find hardest. Verbatim from the authoring design section 2. */
const GUIDANCE_IMAGE_DESCRIBE =
  'Write what a sighted reader learns from this image, in a sentence. “County seal” is a label; “Official seal of Jefferson County, Colorado” is a description. Do not start with “Image of” or “Picture of” — the reader already announces it is a figure. Do not repeat text that is already printed next to the image; it will be read twice.';

/** Branch 2. Short, because the task is. */
const GUIDANCE_IMAGE_REPLACE =
  'Type the words exactly as they appear, and nothing else. A logo reading “CAVU Innovations” takes CAVU Innovations — not “CAVU Innovations logo”. These words are read INSTEAD of the picture, so a listener hearing “logo” hears a word that is not printed on the page.';

/** Branch 3, said plainly so it does not read as giving up. */
const GUIDANCE_IMAGE_DECORATION =
  'Nothing is added to the PDF, and that is correct: a screen reader skips this picture silently instead of stopping on it. Use it for rules, borders, shading and flourishes — anything a sighted reader loses nothing by not seeing.';

/** Section 3 of the authoring design — the case one sentence cannot carry. */
const GUIDANCE_IMAGE_COMPLEX =
  'A chart or a map usually cannot be described in one sentence. The accessible answer is to put the same information on the page as text — a data table beside the chart, or a paragraph of the key figures — then describe the image briefly and point at it: “Bar chart of permit applications by month. The figures are in the table below.”';

/** The refusal, which must offer the way out in the same breath. */
const GUIDANCE_IMAGE_TEXT_REQUIRED =
  'Needed — without it the PDF is not valid, not merely unhelpful. If this picture carries no information, choose “It is decoration” instead; that is a real answer and it needs no text.';

/**
 * The three answers, in the order the authoring design asks them and with its words.
 *
 * `title` is the answer and `hint` is the example set, because an author recognises their
 * own picture faster than they recognise a category. The values are about the image and
 * never about the PDF entry the branch produces.
 */
const IMAGE_PURPOSE_CHOICES: { value: 'describe' | 'replace' | 'decoration'; title: string; hint: string }[] = [
  {
    value: 'describe',
    title: 'It shows something',
    hint: 'a photo, map, chart, diagram, or a seal that means something',
  },
  {
    value: 'replace',
    title: 'It spells out words',
    hint: 'a logo, wordmark, or text drawn as a picture',
  },
  {
    value: 'decoration',
    title: 'It is decoration',
    hint: 'a line, border, shading, or flourish that carries no information',
  },
];

/** The paragraph region's role question, in plain words about the text, not the PDF tag. */
const GUIDANCE_PARAGRAPH_ROLE =
  'Is this body text, or a heading? A heading names a section and lets a screen-reader user jump between sections; body text is read in order. Pick the heading level the way you would in a document — the top title is Heading 1, a section under it is Heading 2, and so on. This is your call: the tool never guesses whether a line is a heading.';

/** The text input's instruction. The words are the content, not a label. */
const GUIDANCE_PARAGRAPH_TEXT =
  'Type the words this region covers, exactly as they appear on the page. They are read by a screen reader in place of the silent graphic the upload drew — nothing is added to what a sighted reader sees. Needed: an empty region tags nothing.';

/** Why a heading level is the author's to choose and skipping one is refused. */
const GUIDANCE_PARAGRAPH_HEADING_WHY =
  'Do not skip a level (Heading 1 then Heading 3): a screen reader reports a gap in the outline. Use Heading 1 for the page title and step down one level at a time.';

/**
 * Body text, then the six heading levels, in the order an author picks them. `title` is what
 * the author sees; `value` is the structure type the backend emits verbatim. Bare `H` is not
 * offered — mixing it with `Hn` fails veraPDF 7.4.4-2.
 */
const PARAGRAPH_ROLE_CHOICES: { value: 'P' | 'H1' | 'H2' | 'H3' | 'H4' | 'H5' | 'H6'; title: string }[] = [
  { value: 'P', title: 'Body text' },
  { value: 'H1', title: 'Heading 1' },
  { value: 'H2', title: 'Heading 2' },
  { value: 'H3', title: 'Heading 3' },
  { value: 'H4', title: 'Heading 4' },
  { value: 'H5', title: 'Heading 5' },
  { value: 'H6', title: 'Heading 6' },
];

/** The small ⓘ glyph that marks a control carrying accessibility guidance. */
const GuidanceMark: React.FC = () => (
  <span className="text-gray-400 cursor-help" aria-hidden="true">&#9432;</span>
);

/**
 * Size a character-block run to the width the user drew.
 *
 * The renderer lays blocks out as
 *     N*blockWidth + (N-1)*blockSpacing + firstGap + secondGap
 * (see PDFViewer's character-block branch), so inverting that for blockWidth is
 * what makes the run finish flush with the field's edge. A preset describes the
 * GROUPING — how many blocks, where the gaps fall, which characters are allowed
 * — and has no business deciding how wide the field is: the user already said
 * that by drawing the rectangle.
 *
 * Math.max keeps a too-narrow field from producing zero or negative blocks; the
 * run then overflows slightly, which is visible and fixable by widening the
 * field — quieter than silently snapping back to a default.
 */
export const fitCharacterBlockConfig = (preset: Record<string, any>, fieldWidth: number) => {
  const total = preset.totalBlocks as number;
  const gaps = (preset.firstGap || 0) + (preset.secondGap || 0);
  const available = (fieldWidth || 0) - (total - 1) * preset.blockSpacing - gaps;
  const blockWidth = Math.max(MIN_BLOCK_WIDTH, Math.floor(available / total));
  return { ...preset, blockWidth };
};

/** The layout a field gets the moment its type becomes Character Block. */
export const defaultCharacterBlockConfig = (fieldWidth: number) =>
  fitCharacterBlockConfig({
    totalBlocks: DEFAULT_TOTAL_BLOCKS,
    blockSpacing: DEFAULT_BLOCK_SPACING,
    firstGap: 0,
    secondGap: 0,
    maxCharsPerBlock: DEFAULT_MAX_CHARS_PER_BLOCK,
    validationPattern: DEFAULT_VALIDATION_PATTERN,
  }, fieldWidth);

/** The options a field gets the moment its type becomes Select. */
export const defaultSelectConfig = () => ({
  options: [
    { value: 'option1', label: 'Option 1', selected: true },
    { value: 'option2', label: 'Option 2', selected: false },
  ],
  allowMultiple: false,
  required: false,
});

/**
 * A field name that was refused, and everything needed to explain the refusal.
 *
 * Carries the author's own text deliberately. Reverting a value someone just typed without
 * showing it back to them reads as the tool eating their work; showing it makes the refusal
 * a statement about the name rather than about them.
 *
 * {@code otherPageNumber} is the part this panel could not previously supply, and the part
 * an author most needs: the field they have collided with may be four pages away and
 * invisible from where they are standing.
 */
export interface NameCollision {
  /** The name the author typed and did not get to keep. */
  rejectedName: string;
  /** The name restored in its place — the field's last non-colliding name, possibly empty. */
  revertedTo: string;
  /** The name of the field already using it. */
  otherName: string;
  /** 1-based page the other field sits on, when known. */
  otherPageNumber?: number;
  /** What both names collapse to in the PDF, which is why they collide at all. */
  pdfName: string;
}

interface FieldPropertiesSidebarProps {
  field: FieldCoordinates;
  /**
   * Live-applies a single property to the working session (no Apply step).
   * A type change also swaps in that type's default config — PDFViewer does
   * both in one update, so don't follow this with a second call to seed one.
   */
  updateField: (property: keyof FieldCoordinates, value: any) => void;
  validationErrors: string[];
  /**
   * Every field on the document, so the name box can tell the author that the name they
   * just typed collides with another one. Needed here rather than computed upstream
   * because the check runs on blur, against the list as it stands at that moment.
   */
  allFields: { id: string; name?: string; pageNumber?: number }[];
  onDelete: () => void;
  onClose: () => void;
  /**
   * Open/closed state, OWNED BY THE PARENT.
   *
   * It used to live in here, on useCollapsiblePanel. It moved out because Esc
   * has to close this panel now that it covers the page, and Esc is a window
   * handler in PDFViewer - which cannot close a panel whose open state it cannot
   * see. The hook is unchanged and still the only mechanism; PDFViewer simply
   * calls it one level up. One source of truth, not two.
   */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /**
   * Render the editors grid as two columns instead of one.
   *
   * THE DECISION IS THE PARENT'S, STATED AS A PROP, AND NOT A VIEWPORT
   * BREAKPOINT — this is the restructuring W3 called for. The old
   * `lg:grid-cols-2` keyed on the WINDOW being 1024px wide, which was a proxy
   * for this panel being wide back when the panel spanned the whole work
   * area. In a ~340px rail that proxy inverts: the window is wide precisely
   * when the panel is narrow, and two 150px columns of inputs is not a
   * layout, it is a ransom note. PDFViewer knows the rail's actual width (it
   * owns the splitter), so it says so here, and the grid obeys the panel's
   * real size at every window size.
   */
  twoColumn?: boolean;
}

/**
 * localStorage key for the inspector's open/closed state. Exported because
 * PDFViewer owns the state now (see `collapsed` above) and has to name the same
 * key; one inspector per editor, so one key is enough.
 */
export const FIELD_PROPS_COLLAPSED_KEY = 'pfbFieldPropsCollapsed';

const inputCls = 'w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-xs font-semibold text-gray-700 mb-1';

/**
 * The field inspector: a FULL-HEIGHT RAIL BESIDE THE PAGE, right of the canvas,
 * scrolling internally, retractable, its width set by the author through a
 * keyboard-operable splitter in PDFViewer (W3 in EDITOR_WORKSPACE_PLAN.md).
 * Every change applies live to the working session; only Save Session persists
 * to the server.
 *
 * THIS SUPERSEDES THE BAND, and the band's own argument is why. The band
 * across the top of the work area (18 Sep – 6 Oct 2026) was itself the
 * resolution of a fight this panel has now had three times: an overlay covers
 * the page ("completely covers the PDF view... making the view of the live
 * property field changes impossible" — QA, 18 Sep), and an in-flow surface
 * reflows it. The band chose reflow, correctly — but it paid for that choice
 * in the page's HEIGHT, which forced PDFViewer to arbitrate between two
 * floors with a rule ("band open, the band wins; band closed, the page wins")
 * that could only ever satisfy one surface at a time. The owner's verdict on
 * the result, 6 Oct: the dropdown is "nice to see all of the info at one
 * time" but "covers too much of the height" — and, fatally for the layout
 * rather than the rule, opening it hid the Form Fields list, the keyboard
 * path and the only reading-order surface (DEFECT-84).
 *
 * THE RAIL DISSOLVES THE DILEMMA INSTEAD OF RE-ARBITRATING IT. Beside the
 * canvas, in the flow, it covers nothing (the occlusion cost that killed the
 * overlay) and it takes no height (the cost that killed the band): the page
 * keeps its full 500px+ floor in every state, the panel gets the full
 * workspace height in every state, and the field list stays on screen in
 * every state. What the rail spends is WIDTH — the plentiful dimension on the
 * landscape monitors this editor targets — and that spend has a real,
 * author-held control: the splitter, with a floor under the panel and a
 * ceiling over it so neither side can crush the other.
 *
 * The band-era right-dock objection ("width is exactly what an author needs
 * to see a page edge to edge") is answered, not ignored: the rail retracts to
 * a 48px pill that genuinely returns its width, the splitter narrows it to
 * its floor, and Fit Width refits the page to whatever is free — pressing it
 * is one click, where the band's height could not be given back at all while
 * a field was selected.
 *
 * WHAT DID NOT MOVE: the field list stays on the left. It is the navigation
 * surface and it is D30's keyboard alternative path to every field, so it has
 * to stay visible while the inspector is open — which the rail finally makes
 * true at every supported width (the drawer and tabbed arrangements below
 * ~1440px are PDFViewer's, documented there).
 */
const FieldPropertiesSidebar: React.FC<FieldPropertiesSidebarProps> = ({
  field,
  updateField,
  allFields,
  validationErrors,
  onDelete,
  onClose,
  collapsed,
  onToggleCollapsed,
  twoColumn = false,
}) => {
  /**
   * The refused name and why, or null. Set on blur, not on every keystroke — see the
   * onChange handler.
   */
  const [nameCollision, setNameCollision] = React.useState<NameCollision | null>(null);

  /**
   * The last name this field held that did not collide, and what a refused edit reverts to.
   *
   * A ref rather than state on purpose: nothing renders from it, and it has to be readable
   * inside the blur handler without that handler depending on a render having happened
   * first. Empty for a field that has never had a valid name, which reverts to empty — the
   * "name required" line then reports it, which is true and already handled.
   */
  const lastGoodName = React.useRef<string>(field.name || '');
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  // A different field is now selected, so any collision shown belongs to the previous one.
  // Without this the warning follows the author to a field it does not describe — and the
  // remembered name would revert THIS field to the previous field's.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- field.id ONLY, deliberately.
  // Adding field.name would refresh the remembered name on every keystroke, so by the time
  // blur ran it would already hold the colliding name and a refusal would revert to it.
  // The remembered name advances in exactly one place: a blur that found no collision.
  React.useEffect(() => {
    setNameCollision(null);
    lastGoodName.current = field.name || '';
  }, [field.id]);
  // Colons are legal in an HTML5 id but break CSS selectors, so they come out.
  const panelId = `pfb-field-props-${useId().replace(/:/g, '')}`;
  const numberChange = (property: keyof FieldCoordinates, min: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      updateField(property, '');
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= min) {
        updateField(property, numValue);
      }
    }
  };

  const numberBlur = (property: keyof FieldCoordinates, min: number, fallback: number) => (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === '' || isNaN(parseInt(e.target.value)) || parseInt(e.target.value) < min) {
      updateField(property, fallback);
    }
  };

  const updateCharBlock = (patch: Record<string, any>) => {
    updateField('characterBlockConfig', { ...field.characterBlockConfig, ...patch });
  };

  /** Field Label tooltip. A radio group gets the extra "this is the question" sentence,
   *  so the tooltip and the inline text below the input never say different things. */
  const fieldLabelGuidance = field.type === 'radio_group'
    ? `${GUIDANCE_FIELD_LABEL} ${GUIDANCE_RADIO_GROUP_LABEL}`
    : GUIDANCE_FIELD_LABEL;

  /**
   * Apply a Quick Preset without resizing the field the user drew.
   *
   * A preset describes the GROUPING — how many blocks, where the gaps fall,
   * what characters are allowed. It has no business deciding how wide the
   * field is: the user already said that by drawing the rectangle. Previously
   * every preset shipped a hard-coded blockWidth of 25pt, so clicking
   * "SSN (3-2-4)" on a field drawn to match a printed form threw that width
   * away and the blocks stopped lining up with the paper.
   *
   * So we keep the preset's grouping and derive the block width to span
   * exactly the drawn width. The renderer lays blocks out as
   *     N*blockWidth + (N-1)*blockSpacing + firstGap + secondGap
   * (see PDFViewer's character-block branch), so inverting that for
   * blockWidth is what makes the run finish flush with the field's edge.
   */
  const applyCharBlockPreset = (preset: Record<string, any>) => {
    // These presets are LAYOUT - how many boxes and where the gaps fall. They must not
    // throw away names the author has typed, so a split block keeps its groups and lets
    // the boxes tally say so if the new block size no longer adds up. An unsplit block
    // has nothing to keep: one group always spans whatever the count becomes.
    const existing = field.characterBlockConfig?.groups;
    const groups = existing && existing.length > 1 ? existing : undefined;
    updateField('characterBlockConfig', fitCharacterBlockConfig({ ...preset, groups }, field.width));
  };

  /* ---------------------------------------------------------------------
   * Character Block groups.
   *
   * A split block emits one comb field per group, so each group carries its own
   * accessible name. resolveCharacterBlockGroups is the only way this component
   * reads them - it guarantees a non-empty list, so the editor below never has a
   * "no groups yet" state to special-case.
   *
   * With ONE group the name column is not shown at all: the field label above is
   * already required and already names it. Adding a second group is what makes
   * group names appear and become required - nobody should have to type "Social
   * Security Number" twice to describe one undivided block.
   * ------------------------------------------------------------------------- */
  const charBlockGroups = resolveCharacterBlockGroups(field.characterBlockConfig);
  const charBlockTotal = Number(field.characterBlockConfig?.totalBlocks) || 0;
  const charBlockBoxes = characterBlockBoxCount(charBlockGroups);
  const charBlockBoxesMatch = charBlockTotal > 0 && charBlockBoxes === charBlockTotal;
  const charBlockNamed = characterBlockNamedCount(charBlockGroups);
  const charBlockWarnings = characterBlockGroupWarnings(charBlockGroups, field.label);

  /** Writes the group list. Collapsing back to one group drops its name, because a
   *  single group is named by the field label and a leftover name would be invisible
   *  state that nothing displays. */
  const writeCharBlockGroups = (groups: CharacterBlockGroup[]) => {
    updateCharBlock({
      groups: groups.length === 1 ? [{ boxes: groups[0].boxes }] : groups,
    });
  };

  /** A new group takes one box from the last one where it can, so the tally stays
   *  balanced instead of turning red the instant the author clicks Add. */
  const addCharBlockGroup = () => {
    const groups = charBlockGroups.map(group => ({ ...group }));
    const last = groups[groups.length - 1];
    if ((Number(last.boxes) || 0) > 1) {
      last.boxes = Number(last.boxes) - 1;
    }
    groups.push({ boxes: 1, label: '' });
    writeCharBlockGroups(groups);
  };

  /** Removing a group hands its boxes to a neighbour, for the same reason. */
  const removeCharBlockGroup = (index: number) => {
    if (charBlockGroups.length <= 1) return;
    const groups = charBlockGroups.map(group => ({ ...group }));
    const [removed] = groups.splice(index, 1);
    const neighbour = groups[index - 1] || groups[index] || groups[0];
    neighbour.boxes = (Number(neighbour.boxes) || 0) + (Number(removed.boxes) || 0);
    writeCharBlockGroups(groups);
  };

  const setCharBlockGroup = (index: number, patch: Partial<CharacterBlockGroup>) => {
    writeCharBlockGroups(charBlockGroups.map((group, i) => (i === index ? { ...group, ...patch } : group)));
  };

  /**
   * Applies a preset the author explicitly picked - never a pre-fill. It sets the
   * groups AND their names, resizes the block to match, and puts the drawn gaps on
   * the group boundaries so what is printed matches what is announced.
   */
  const applyCharBlockGroupPreset = (id: string) => {
    const preset = CHARACTER_BLOCK_GROUP_PRESETS.filter(p => p.id === id)[0];
    if (!preset) return;
    const groups = preset.groups.map(group => ({ ...group }));
    const totalBlocks = characterBlockBoxCount(groups);
    updateField('characterBlockConfig', fitCharacterBlockConfig({
      ...field.characterBlockConfig,
      totalBlocks,
      groups,
      blockSpacing: field.characterBlockConfig?.blockSpacing ?? DEFAULT_BLOCK_SPACING,
      maxCharsPerBlock: field.characterBlockConfig?.maxCharsPerBlock ?? DEFAULT_MAX_CHARS_PER_BLOCK,
      ...gapLayoutForGroups(groups),
    }, field.width));
  };

  const updateRadioOptions = (options: any[]) => {
    updateField('radioGroupConfig', { ...field.radioGroupConfig, options });
  };

  const updateSelectOptions = (options: any[]) => {
    updateField('selectConfig', { ...field.selectConfig, options });
  };

  /** "Required" carries a different meaning on a checkbox; see GUIDANCE_REQUIRED_CHECKBOX. */
  const isCheckbox = field.type === 'checkbox';

  /**
   * An image region takes NO field label, NO required flag and NO format hint, so the whole
   * accessibility column is wrong for it: its one string is either a description of a picture
   * or a substitute for one, and it is collected in the region's own block below. Showing the
   * Field Label box here would ask the author to name a control that is not a control, and
   * the orange "needed for screen readers" prompt under it would be asking for the wrong
   * thing in the one place they are most likely to believe it.
   */
  const isImageRegion = field.type === 'image_region';

  /** Two of the three branches take a sentence; decoration takes nothing. */
  const imageRegionNeedsText = isImageRegion
    && (field.imageRegionConfig?.purpose === 'describe'
        || field.imageRegionConfig?.purpose === 'replace');

  /** The instruction changes with the branch: describing and quoting are different tasks. */
  const imageRegionTextGuidance = field.imageRegionConfig?.purpose === 'replace'
    ? GUIDANCE_IMAGE_REPLACE
    : GUIDANCE_IMAGE_DESCRIBE;

  /**
   * A paragraph region, like an image region, takes NO field label, NO required flag and NO
   * format hint: it is body content, not a control. Its two inputs — the role and the text —
   * are collected in the region's own block below, and the generic accessibility column is
   * suppressed for it for the same reason it is for an image region.
   */
  const isParagraph = field.type === 'paragraph';

  /** A paragraph always needs its text — both body and heading roles carry words. */
  const paragraphNeedsText = isParagraph;

  /**
   * An option label is REQUIRED once a group has more than one option — the same rule, for the
   * same reason, as character-block group names: two unnamed options produce byte-identical
   * announcements, and a listener cannot tell which button they are on. A single-option group
   * is exempt because the field label already names it.
   */
  const radioOptionLabelMissing = (index: number): boolean => {
    const options = field.radioGroupConfig?.options || [];
    if (options.length < 2) return false;
    const label = options[index]?.label;
    return !label || !label.trim();
  };

  if (collapsed) {
    // The retracted rail: a slim VERTICAL pill, the exact shape the field
    // list already collapses to on the opposite edge, because an author who
    // learns "panels retract to a labelled sliver on their own edge" on one
    // side should find the same grammar on the other. Retracted it costs the
    // page 48px of width and nothing else — the rest genuinely returns to the
    // canvas, which is what a collapse control is for.
    //
    // Width ~48px, height well past it: comfortably beyond WCAG 2.5.8's
    // 24x24 target minimum.
    //
    // At drawer widths Esc DOES retract the open panel to this pill — see the
    // Esc chain in PDFViewer, which regained that job when the panel went
    // back to covering the canvas edge there. This pill itself is a real
    // <button> with aria-expanded and aria-controls, which is the accessible
    // route back and always was.
    return (
      <button
        onClick={onToggleCollapsed}
        title="Show field properties"
        aria-expanded={false}
        aria-controls={panelId}
        className="self-start w-12 flex-shrink-0 flex flex-col items-center gap-2 px-2 py-4 bg-white border-2 border-gray-200 rounded-xl shadow-sm hover:border-indigo-300 transition-all duration-200"
      >
        <svg className="w-5 h-5 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-xs font-bold text-gray-700 [writing-mode:vertical-rl]">
          Field Properties
        </span>
      </button>
    );
  }

  // THE OPEN RAIL.
  //
  // IT SCROLLS INTERNALLY, and its DIMENSIONS LIVE IN PDFViewer, not here —
  // the parent's cell supplies the height (the full work area, in every mode)
  // and the width (the splitter's value, or the drawer/tab slot). That is
  // deliberate, and it is the same division of labour the band kept: how much
  // room the editor gives this panel is a layout decision about the editor,
  // not a fact about field properties. Putting a dimension here is how it
  // once ended up as a 45vh number that meant something different on every
  // monitor. This panel just fills the slot it is given and scrolls inside
  // it — and because the slot is now the workspace's full HEIGHT rather than
  // a capped band of it, the "see all of the info at one time" quality the
  // owner valued survives in a surface that no longer costs the page a pixel
  // of height.
  //
  // The internal scroll is a deliberate departure from the "never scrolls"
  // rule in section 2.A of the old spec, and that rule still stands where it
  // was written: it governs the SHALLOW 64px bar, whose entire discipline was
  // that it held only what fits in one row. This is the FULL inspector — a
  // character-block field carries a group editor with one row per group, and
  // no fixed height holds that. The nested-scroll defect 66ef98d removed was
  // a scroller with NO height cap inside another scroller, reachable only by
  // scrolling twice; the parent cell's bounded height is what makes this
  // different.
  //
  // Solid background, real border, real shadow. NOT translucent - see section 3.2
  // of the spec: translucency makes both layers harder to read and puts WCAG
  // 1.4.3 contrast at the mercy of whatever happens to be on the page underneath.
  // (At drawer widths this once again sits over the canvas edge, so this is no
  // longer belt and braces — it is load-bearing there.)
  //
  // NOT a dialog: no aria-modal, no focus trap, no inert on the rest of the page,
  // and tab leaves in both directions. The whole point is to see the field while
  // its size is being edited, which a modal would make impossible.
  //
  // flex-1 min-h-0 is what makes the parent cell's height reach this box: without
  // flex-1 it takes its content height and overflows a shrunken wrapper; without
  // min-h-0 the body's scroller never engages. Both, or neither works.
  return (
    <div
      id={panelId}
      role="region"
      aria-label="Field properties"
      className="w-full bg-white border-2 border-gray-200 rounded-xl shadow-2xl flex flex-col flex-1 min-h-0"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
        <div className="flex items-center text-sm font-bold text-gray-900 min-w-0">
          <svg className="w-4 h-4 mr-2 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="flex-shrink-0">Field Properties</span>
          {/* The field is named in the header because the rail is not glued to
              the field it describes — adjacency implies nothing across a canvas,
              so the panel has to say WHICH field it is about. */}
          <span className="ml-2 font-normal text-gray-500 truncate" title={field.name}>
            {field.name || field.id}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onToggleCollapsed}
            title="Retract field properties"
            aria-expanded={true}
            aria-controls={panelId}
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
          >
            {/* Right, not up: the rail retracts toward the right edge, and the
                icon has to say which way it goes — the mirror of the field
                list's leftward chevrons on the opposite edge. p-1.5 on a 16px
                icon is a 28px target, past WCAG 2.5.8's 24x24 minimum rather
                than sitting exactly on it. */}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={onClose}
            title="Deselect field"
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Validation summary — changes are live, so surface problems prominently */}
      {/*
        THE REFUSED NAME GOES AT THE TOP, in its own bar above the validation one.

        Not under the name box, where it lived until 2 October 2026 and where a tester
        looking for exactly this missed it — an author placing fields reads the page, not
        this panel's interior. The owner confirmed this bar's treatment is the one that
        does get noticed, so this reuses it rather than inventing a third notification
        surface.

        Red rather than the validation bar's orange, and worded in the past tense,
        because the two are different kinds of statement: validation says "this is not
        finished yet", this says "what you just typed was not applied". role="alert" with
        aria-live="assertive" gives a screen-reader user the same interruption a sighted
        author gets from the colour — which this product of all products should get right.
      */}
      {nameCollision && (
        <div
          role="alert"
          aria-live="assertive"
          className="px-3 py-2 bg-red-50 border-b-2 border-red-400 text-[11px] text-red-800"
        >
          <div className="font-bold">Field name not changed</div>
          <div className="mt-0.5">
            &ldquo;<span className="font-mono">{nameCollision.rejectedName}</span>&rdquo; is
            already used by{' '}
            <span className="font-semibold">
              {nameCollision.otherName || 'another field'}
            </span>
            {nameCollision.otherPageNumber !== undefined
              ? ` on page ${nameCollision.otherPageNumber}`
              : ''}
            .{' '}
            {nameCollision.revertedTo
              ? <>The name is back to &ldquo;<span className="font-mono">{nameCollision.revertedTo}</span>&rdquo;.</>
              : <>This field still has no name.</>}{' '}
            Type a different one to continue.
          </div>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="px-3 py-2 bg-orange-50 border-b border-orange-200 text-[11px] text-orange-700">
          {validationErrors.map((err, i) => (
            <div key={i}>• {err}</div>
          ))}
          <div className="mt-1 text-orange-500">Fix before saving the session.</div>
        </div>
      )}

      {/* EDITORS - TWO LABELLED COLUMNS, `properties` and `accessibility`.

          Scott, 16 Sep: "Review the accessible items within its own grouping would
          be ideal." This is that grouping. Whether the two groups sit SIDE BY SIDE
          or stacked is the parent's call through `twoColumn` (see the prop's note:
          the panel's own width, not the window's, is the fact that matters), but
          the grouping itself survives at every width — in a single column the two
          sections simply stack, each still under its own heading, which is what
          the old 256px rail never had: there the accessibility controls were
          interleaved through one unlabelled stack in field order, each one
          individually well written and collectively invisible as a group.

          WHAT COUNTS AS ACCESSIBILITY here is the tier distinction in section 8.1 of
          NAMING_AUDIT_PFB_VS_PDFUA.md: "anything a screen reader speaks, or that
          exists only so a screen reader can speak it". That is the Field Label (the
          accessible name, /TU), Required (the printed asterisk is page image and
          cannot be read, so this checkbox is the only thing that says "mandatory"),
          and the Format Hint (read out WITH the field name). All three were already
          annotated as accessibility in this file. None of them read as a group.

          NO MICROCOPY CHANGES. The wording is approved and lives in
          PDFUA_FIELD_PROPERTY_UI_SPEC.md; this is layout only.

          The per-group accessible names inside the character-block and radio editors
          stay where they are, in the full-width block below. They are per-ROW
          controls of a positional editor - group 3's name beside group 3's box count
          - and lifting them out of their rows would break the sum-equals-total
          invariant that editor exists to show. The column heading covers the
          FIELD-level controls; the row-level ones stay attached to their rows.

          In a narrow panel it is one column, and the decision keys on the
          PANEL's width via `twoColumn`, never the viewport's — the old
          `lg:grid-cols-2` here is what W3 retired, because a wide window is
          exactly when this panel is a ~340px rail. The reflow obligation
          (WCAG 1.4.10) is satisfied for free: a narrow viewport cannot hold a
          wide panel, so `twoColumn` is false there too. */}
      <div className="overflow-y-auto flex-1 min-h-0 p-3">
        <div className={`grid ${twoColumn ? 'grid-cols-2' : 'grid-cols-1'} gap-x-6 gap-y-3 items-start`}>

          <section aria-labelledby={`${panelId}-props-heading`} className="space-y-3 min-w-0">
            <h4
              id={`${panelId}-props-heading`}
              className="text-[11px] font-bold uppercase tracking-wide text-gray-500 border-b border-gray-200 pb-1"
            >
              Field properties
            </h4>

          {/* Name */}
          <div>
            <label className={labelCls} htmlFor={FIELD_PROPS_NAME_INPUT_ID}>Field Name</label>
            <input
              id={FIELD_PROPS_NAME_INPUT_ID}
              ref={nameInputRef}
              type="text"
              value={field.name}
              onChange={(e) => {
                // Clear a stale collision as soon as the author starts editing. Re-checking
                // on every keystroke would flag every half-typed name that happens to
                // match, which is noise while they are still typing.
                if (nameCollision) setNameCollision(null);
                updateField('name', e.target.value);
              }}
              /*
                REFUSE THE EDIT, do not merely complain about it.
                ---------------------------------------------------------------------------
                This used to set a warning and leave the colliding name in place. Measured
                consequence, QA 1-2 October 2026: duplicates accumulated, and because
                field_configurations upserts on (documentId, fieldName), BOTH fields then
                wrote to ONE row on save. The second overwrote the first's geometry and
                properties, the row's id was recorded twice in field_configuration_ids, and
                the next load produced two entries sharing one id. After that the editor
                cannot tell them apart: a rename hits both, and deleting one behaves
                unpredictably. One advisory warning cost a field's configuration.

                So the name reverts and focus comes back with the text selected. Nothing is
                disabled — one edit simply did not take, and the author is standing exactly
                where they need to be to change it. Returning focus ONCE is attention;
                refusing to let them leave would be a cage.
              */
              onBlur={() => {
                const other = findFieldNameCollision(allFields, field);
                if (!other) {
                  lastGoodName.current = field.name || '';
                  setNameCollision(null);
                  return;
                }
                const rejectedName = (field.name || '').trim();
                setNameCollision({
                  rejectedName,
                  revertedTo: lastGoodName.current,
                  otherName: other.name || '',
                  otherPageNumber: allFields.find((f) => f.id === other.id)?.pageNumber,
                  pdfName: pdfFieldName(rejectedName),
                });
                updateField('name', lastGoodName.current);
                // After the revert has rendered, or the selection lands on the old text.
                requestAnimationFrame(() => {
                  nameInputRef.current?.focus();
                  nameInputRef.current?.select();
                });
              }}
              spellCheck={false}
              aria-invalid={!isValidFieldName(field.name || '') || nameCollision !== null}
              aria-describedby={nameCollision ? FIELD_PROPS_NAME_ERROR_ID : undefined}
              className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:ring-2 ${
                !isValidFieldName(field.name || '') || nameCollision
                  ? 'border-orange-400 focus:ring-orange-400 bg-orange-50'
                  : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
              }`}
              placeholder="Enter field name"
            />
            {!isValidFieldName(field.name || '') && (
              <p className="text-[10px] text-orange-600 mt-0.5">
                {(field.name || '').trim() === ''
                  ? 'Field name required for PDF generation'
                  : 'Letters, numbers, underscore and hyphen only — no spaces. The name is the PDF’s internal identity; human wording belongs in the Label.'}
              </p>
            )}
            {/*
              The collision message names the other field rather than saying "duplicate",
              because the two names may not look alike: "First Name" and "first.name" both
              become "first_name" in the PDF, and an author staring at two visibly
              different names needs telling WHICH one and WHY.

              No auto-suffix. The name is the author's to choose, and silently turning
              their "address" into "address_2" is the tool inventing a value - the rule
              that removed generateLabelFromName from this product.
            */}
            {nameCollision && (
              <p
                id={FIELD_PROPS_NAME_ERROR_ID}
                className="text-[10px] text-red-700 mt-0.5"
              >
                Both become{' '}
                <span className="font-mono">{nameCollision.pdfName}</span> in the PDF, which
                makes them one field that fills in both places.
              </p>
            )}
          </div>

          {/* Type */}
          <div>
            <label className={labelCls}>Field Type</label>
            <select
              value={field.type}
              onChange={(e) => {
                // Just set the type. PDFViewer swaps in that type's default
                // config in the same update — including the character-block
                // layout, which the renderer needs before it will draw anything.
                updateField('type', e.target.value as any);
              }}
              className={inputCls}
            >
              <option value="character_block">Character Block</option>
              <option value="checkbox">Checkbox</option>
              <option value="date">Date</option>
              <option value="image_region">Image Region</option>
              <option value="email">Email</option>
              <option value="number">Number</option>
              <option value="paragraph">Paragraph</option>
              <option value="push_button">Push Button</option>
              <option value="radio_group">Radio Button Group</option>
              <option value="select">Select</option>
              <option value="signature_line">Signature Line</option>
              <option value="text">Text</option>
              <option value="textarea">Text Area</option>
            </select>
          </div>


          {/* Position */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>X</label>
                {/* Deliberately NO onWheel guard here. Wheel-adjust on a FOCUSED
                    number input is a genuinely useful way to nudge a coordinate,
                    and this sidebar is a power tool used by someone who drew the
                    field.

                    OBSERVED 2026-09-06: hovering an UNFOCUSED field scrolls the
                    panel and leaves the value alone; once the field is clicked,
                    the wheel adjusts it. That is the behaviour we want, and it
                    needs no code - it is what the browser already does.

                    But wheel-on-number is NOT in the HTML spec, it is a browser
                    convenience: Safari historically does not implement it at all,
                    and unspecified behaviour can change. So this was verified in
                    one browser, not guaranteed everywhere. If an UNFOCUSED field
                    is ever seen to change, add a focus check here rather than the
                    blanket blur() guard - that would take the feature away.

                    The generated forms and the Live Form Preview DO guard it -
                    there the person is filling in a form, not positioning fields,
                    and a silently changed answer is a data-integrity bug. */}
              {/*
                Number.isFinite, NOT a truthiness test. `field.x ? … : ''` rendered a
                coordinate of 0 as an EMPTY BOX, and 0 is a legal position — the left edge
                of the page. The handlers always accepted it: numberChange('x', 0) and
                numberBlur('x', 0, 0) both take 0 as the minimum and the default. Only the
                display refused to show it.

                Same defect as the character block's spacing, fixed 1 October, and it
                matters more here: these two inputs are the ONLY way to position a field
                without a mouse, so they are what a keyboard-only or screen-reader author
                depends on. A coordinate that reads blank when it is really 0 is worse than
                useless to someone who cannot see where the box landed.

                isFinite rather than ?? because Math.round(undefined) is NaN, which React
                renders as the text "NaN". This gives 0 for 0, and empty for absent.
              */}
              <input
                type="number"
                value={Number.isFinite(field.x) ? Math.round(field.x) : ''}
                onChange={numberChange('x', 0)}
                onBlur={numberBlur('x', 0, 0)}
                className={inputCls}
                placeholder="X"
              />
            </div>
            <div>
              <label className={labelCls}>Y</label>
              <input
                type="number"
                value={Number.isFinite(field.y) ? Math.round(field.y) : ''}
                onChange={numberChange('y', 0)}
                onBlur={numberBlur('y', 0, 0)}
                className={inputCls}
                placeholder="Y"
              />
            </div>
            <div>
              <label className={labelCls}>Width</label>
              <input
                type="number"
                value={Math.round(field.width) || ''}
                onChange={numberChange('width', 1)}
                onBlur={numberBlur('width', 1, 100)}
                className={inputCls}
                placeholder="Width"
                min="1"
              />
            </div>
            <div>
              <label className={labelCls}>Height</label>
              <input
                type="number"
                value={Math.round(field.height) || ''}
                onChange={numberChange('height', 1)}
                onBlur={numberBlur('height', 1, 30)}
                className={inputCls}
                placeholder="Height"
                min="1"
              />
            </div>
          </div>

            {/* Page. Read-only, and NOT an accessibility control - it is where the
                field is. That is why it stays in this column while Required, which
                shared a row with it before, moved to the other one. */}
            <div className="w-20">
              <label className={labelCls}>Page</label>
              <input type="number" value={field.pageNumber} readOnly className={`${inputCls} bg-gray-100`} />
            </div>

          {/* Default value. The format hint that used to share this comment is an
              accessibility control and now sits in the other column. */}
          <div>
            <label className={labelCls}>Default Value</label>
            <input
              type="text"
              value={field.defaultValue || ''}
              onChange={(e) => updateField('defaultValue', e.target.value)}
              className={inputCls}
              placeholder="Enter default value"
            />
          </div>

          {/* Data Key — the OPTIONAL shared data binding. Fields that carry the SAME Data Key
              fill together: type the value once and it appears in every one (e.g. a VIN repeated
              on each page of a form). Leave it blank for an ordinary, independent field. It is a
              data-layer concern only — silent to assistive technology, and distinct from both the
              Field Name (identity) and the Field Label (announced name). See
              docs/design/FIELD_DATA_BINDING_DESIGN.md. */}
          <div>
            <label
              className={labelCls}
              title="Optional. Link several fields to one value: give them the same Data Key and they fill together (type once, fills everywhere). Leave blank for an independent field."
            >
              Data Key
            </label>
            <input
              type="text"
              value={field.dataKey || ''}
              onChange={(e) => updateField('dataKey', e.target.value)}
              className={inputCls}
              placeholder="Optional — share one value across fields"
            />
          </div>
          </section>

          <section aria-labelledby={`${panelId}-a11y-heading`} className="space-y-3 min-w-0">
            {/* The heading says what the column IS FOR, not what it is called. An
                author who has never read a success criterion still knows what "what a
                screen reader announces" means, and that is the whole test. */}
            <h4
              id={`${panelId}-a11y-heading`}
              className="text-[11px] font-bold uppercase tracking-wide text-indigo-700 border-b border-indigo-200 pb-1 flex items-center gap-1.5"
            >
              <GuidanceMark />
              Accessibility &mdash; what a screen reader announces
            </h4>

          {/* Field Label = the ACCESSIBLE NAME. Becomes /TU in the PDF and the
              <label> in the generated web form. It is NEVER drawn on the PDF -
              the visible label is page content from the uploaded document. A
              screen reader cannot read that, so this is the only name it gets.
              Must match the printed text: WCAG 2.5.3 Label in Name. */}
          {!isImageRegion && !isParagraph && (
          <div>
            <label className={labelCls} title={fieldLabelGuidance}>
              Field Label <GuidanceMark />
            </label>
            <input
              type="text"
              value={field.label || ''}
              onChange={(e) => updateField('label', e.target.value)}
              className={inputCls}
              title={fieldLabelGuidance}
              placeholder="Exactly as printed on the form"
            />
            {(!field.label || field.label === field.name) ? (
              <p className="text-[10px] text-orange-600 mt-0.5">{GUIDANCE_FIELD_LABEL_PROMPT}</p>
            ) : (
              <p className="text-[10px] text-gray-500 mt-0.5">
                Announced by screen readers. Must match the text printed on the form.
              </p>
            )}
            {/* A radio group is ONE field: its label is the question, and every
                option inherits it. Say so where the label is typed, not in a doc. */}
            {field.type === 'radio_group' && (
              <p className="text-[10px] text-gray-600 mt-0.5">{GUIDANCE_RADIO_GROUP_LABEL}</p>
            )}
          </div>
          )}

            {/* Required is an accessibility control, not just a validation one: the
                printed asterisk is page image, so this checkbox is what actually tells
                a screen reader the field is mandatory. That is why it is in this
                column now and not beside Page, where it used to sit.

                ON A CHECKBOX IT MEANS SOMETHING ELSE, so it is worded differently.
                "Required" on a text field means "supply a value". On a checkbox it can
                only mean "must be ticked", because an unticked box is indistinguishable
                from an unanswered one - see GUIDANCE_REQUIRED_CHECKBOX. Same stored
                property, same enforcement; only the wording narrows, so that an author
                reaching for a yes/no question is sent to a Radio Group instead. */}
            {/* REQUIRED IS NOT OFFERED ON AN IMAGE REGION, and the omission is the point:
                nobody fills in a picture. "Required" there would not be a weaker claim, it
                would be a meaningless one, and the control would appear in the required-field
                machinery of both the PDF and the generated web form. */}
            {!isImageRegion && !isParagraph && (
            <div>
              <label
                className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none"
                title={isCheckbox ? GUIDANCE_REQUIRED_CHECKBOX : GUIDANCE_REQUIRED}
              >
                <input
                  type="checkbox"
                  checked={field.required || false}
                  onChange={(e) => updateField('required', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  title={isCheckbox ? GUIDANCE_REQUIRED_CHECKBOX : GUIDANCE_REQUIRED}
                />
                {isCheckbox ? 'Must be ticked to submit' : 'Required field'} <GuidanceMark />
              </label>
              <p className="text-[10px] text-gray-500 mt-0.5">
                {isCheckbox ? GUIDANCE_REQUIRED_CHECKBOX : GUIDANCE_REQUIRED}
              </p>
            </div>
            )}

            {/* Read-only sits beside Required because they are the same kind of control:
                one /Ff bit each, both announced as state, neither of them visible on the
                page. It is hidden on an image region for the same reason Required is —
                nobody fills in a picture, so nobody is stopped from filling one in.

                ONE CONTROL, DELIBERATELY. The author says "this value cannot be changed"
                and the generator writes bit 1; there is nothing else to configure. */}
            {!isImageRegion && !isParagraph && (
            <div>
              <label
                className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none"
                title={GUIDANCE_READ_ONLY}
              >
                <input
                  type="checkbox"
                  checked={field.readOnly || false}
                  onChange={(e) => updateField('readOnly', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  title={GUIDANCE_READ_ONLY}
                />
                Read-only field <GuidanceMark />
              </label>
              <p className="text-[10px] text-gray-500 mt-0.5">{GUIDANCE_READ_ONLY}</p>
            </div>
            )}

          {/* Format Hint is read out WITH the field name, so it is accessibility
              copy, not decoration. It is the only legal home for a date format:
              a PDF has no placeholder entry to put one in. */}
          {!isImageRegion && !isParagraph && (
          <div>
            <label className={labelCls} title={GUIDANCE_FORMAT_HINT}>
              Format Hint <GuidanceMark />
            </label>
            <input
              type="text"
              value={field.formatHint || ''}
              onChange={(e) => updateField('formatHint', e.target.value)}
              className={inputCls}
              title={GUIDANCE_FORMAT_HINT}
              placeholder="e.g. MM/DD/YYYY"
            />
            <p className="text-[10px] text-gray-500 mt-0.5">{GUIDANCE_FORMAT_HINT}</p>
          </div>
          )}

            {/* MOVED HERE 25 September 2026, from below both columns.

                It rendered full-width under the closed grid, which left this column
                showing its heading and nothing beneath it — every other child here is
                wrapped in {!isImageRegion && …}, so for an image region the heading
                stood alone while its controls sat somewhere else entirely. An author
                meeting the control for the first time read "what a screen reader
                announces", found nothing under it, and had to discover the real
                question at the bottom left.

                The placement is not only tidier, it is the honest one: "what does this
                image do?" and the text it produces ARE what a screen reader announces.
                They were never type-specific configuration in the sense the block below
                collects — they are this control's entire accessibility content. */}
          {/* IMAGE REGION — the three-way question, and one text box for two of the three
              branches. This is the ONLY control whose accessibility inputs are not the Field
              Label: a region's text is either a DESCRIPTION of a picture or a SUBSTITUTE for
              one, and which it is changes what the file says. A field label is neither.

              The author is never shown "Alt", "ActualText" or "artifact". Those are format
              keys; the question is about the picture. Branch 1 and branch 2 are told apart
              only by a human (Matterhorn 13-005 / 13-008), so this wording is the whole of
              the mechanism. */}
          {field.type === 'image_region' && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded space-y-2">
              <p className="text-[10px] text-blue-800" title={GUIDANCE_IMAGE_REGION}>
                {GUIDANCE_IMAGE_REGION}
              </p>
              <fieldset>
                <legend
                  className="text-[11px] font-semibold text-gray-700 mb-1 flex items-center gap-1.5"
                  title={GUIDANCE_IMAGE_PURPOSE}
                >
                  What does this image do? <GuidanceMark />
                </legend>
                {IMAGE_PURPOSE_CHOICES.map(choice => (
                  <label
                    key={choice.value}
                    className="flex items-start gap-2 text-[11px] text-gray-800 cursor-pointer select-none py-0.5"
                  >
                    <input
                      type="radio"
                      name={`${panelId}-image-purpose`}
                      value={choice.value}
                      checked={field.imageRegionConfig?.purpose === choice.value}
                      onChange={() => updateField('imageRegionConfig', {
                        // The text survives a change of mind between the two EMITTING
                        // branches, because the author usually meant to reclassify rather
                        // than to retype. Nothing is discarded on the way to 'decoration'
                        // either: the emitter ignores the text there, and dropping it would
                        // punish a mis-click by deleting a sentence.
                        ...field.imageRegionConfig,
                        purpose: choice.value,
                      })}
                      className="mt-0.5 w-3.5 h-3.5 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span>
                      <span className="font-semibold">{choice.title}</span>
                      <span className="text-gray-600"> — {choice.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>

              {/* ONE box, for the two branches that need one. Its label and its guidance
                  change with the branch, because "describe the picture" and "type the words
                  exactly" are different instructions and a single generic prompt gets one of
                  them wrong. */}
              {imageRegionNeedsText && (
                <div>
                  <label className={labelCls} title={imageRegionTextGuidance}>
                    {field.imageRegionConfig?.purpose === 'replace'
                      ? 'The words in the picture'
                      : 'What this image shows'}{' '}
                    <GuidanceMark />
                  </label>
                  <input
                    type="text"
                    value={field.imageRegionConfig?.text || ''}
                    onChange={(e) => updateField('imageRegionConfig', {
                      ...field.imageRegionConfig,
                      purpose: field.imageRegionConfig?.purpose || 'describe',
                      text: e.target.value,
                    })}
                    className={inputCls}
                    title={imageRegionTextGuidance}
                    placeholder={field.imageRegionConfig?.purpose === 'replace'
                      ? 'e.g. Jefferson County Clerk and Recorder'
                      : 'e.g. Official seal of Jefferson County, Colorado'}
                  />
                  {/* The refusal names the way out. Left as a dead end, an author types a
                      space to get past it — and a space passes nothing: an empty /Alt fails
                      the validator outright. */}
                  {!(field.imageRegionConfig?.text || '').trim() ? (
                    <p className="text-[10px] text-orange-600 mt-0.5">{GUIDANCE_IMAGE_TEXT_REQUIRED}</p>
                  ) : null}
                  <p className="text-[10px] text-blue-700 mt-0.5">{imageRegionTextGuidance}</p>
                  {/* Section 3 of the authoring design: the case one sentence cannot carry.
                      Guidance, not machinery — and deliberately NOT a long-description field,
                      which would write something no reader announces and let the author
                      believe they had solved it. */}
                  {field.imageRegionConfig?.purpose === 'describe' && (
                    <p className="text-[10px] text-gray-600 mt-1">{GUIDANCE_IMAGE_COMPLEX}</p>
                  )}
                </div>
              )}
              {field.imageRegionConfig?.purpose === 'decoration' && (
                <p className="text-[10px] text-blue-700">{GUIDANCE_IMAGE_DECORATION}</p>
              )}
            </div>
          )}

          {/* PARAGRAPH — the role question, then the words. Like the image region, its
              accessibility inputs are NOT the Field Label: the text is the body content the
              region tags, not a control's name. The role is the author's semantic call (body
              text vs a heading level), which the tool emits and never infers — the upload audit
              suggests, the author acts. */}
          {field.type === 'paragraph' && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded space-y-2">
              <fieldset>
                <legend
                  className="text-[11px] font-semibold text-gray-700 mb-1 flex items-center gap-1.5"
                  title={GUIDANCE_PARAGRAPH_ROLE}
                >
                  What is this text? <GuidanceMark />
                </legend>
                {PARAGRAPH_ROLE_CHOICES.map(choice => (
                  <label
                    key={choice.value}
                    className="flex items-center gap-2 text-[11px] text-gray-800 cursor-pointer select-none py-0.5"
                  >
                    <input
                      type="radio"
                      name={`${panelId}-paragraph-role`}
                      value={choice.value}
                      checked={field.paragraphConfig?.role === choice.value}
                      onChange={() => updateField('paragraphConfig', {
                        // The text survives a change of role: the author usually meant to
                        // reclassify the same words, not to retype them.
                        ...field.paragraphConfig,
                        role: choice.value,
                      })}
                      className="w-3.5 h-3.5 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="font-semibold">{choice.title}</span>
                  </label>
                ))}
              </fieldset>

              {/* Why the level is the author's, and why skipping one is wrong. Shown only once
                  a heading role is picked, so a body paragraph is not told about levels. */}
              {field.paragraphConfig?.role && field.paragraphConfig.role !== 'P' && (
                <p className="text-[10px] text-gray-600">{GUIDANCE_PARAGRAPH_HEADING_WHY}</p>
              )}

              {paragraphNeedsText && (
                <div>
                  <label className={labelCls} title={GUIDANCE_PARAGRAPH_TEXT}>
                    The words this region covers <GuidanceMark />
                  </label>
                  <textarea
                    rows={3}
                    value={field.paragraphConfig?.text || ''}
                    onChange={(e) => updateField('paragraphConfig', {
                      ...field.paragraphConfig,
                      // A paragraph defaults to body text if the author typed before picking a
                      // role — the commonest case, and still their call to change.
                      role: field.paragraphConfig?.role || 'P',
                      text: e.target.value,
                    })}
                    className={inputCls}
                    title={GUIDANCE_PARAGRAPH_TEXT}
                    placeholder="e.g. The Board will hold a public hearing on the proposed budget."
                  />
                  {/* The refusal names no way out here because there is none: unlike an image, a
                      paragraph with no words tags nothing, and "mark it decoration" is not an
                      answer for body text. */}
                  {!(field.paragraphConfig?.text || '').trim() ? (
                    <p className="text-[10px] text-orange-600 mt-0.5">
                      Needed — an empty region tags nothing, which is the untagged text it was
                      drawn to fix.
                    </p>
                  ) : null}
                  <p className="text-[10px] text-blue-700 mt-0.5">{GUIDANCE_PARAGRAPH_TEXT}</p>
                </div>
              )}
            </div>
          )}
          </section>
        </div>

        {/* FULL WIDTH, below both columns: the type-specific configuration and the
            appearance controls. They are here rather than inside a column because
            they are the tallest and widest thing in the panel - the character-block
            group editor is one row per group - and a column would make them a narrow
            stack again, which is the defect this whole change removes. */}
        <div className="mt-3 space-y-3">
        {/* SIGNATURE LINE — guidance only. The control takes ONE author input and it
            is the Field Label above, which becomes the PrintField /Desc. There is
            nothing else to configure and nothing this panel could offer that would
            not be an invented accessibility value. */}
        {field.type === 'signature_line' && (
          <div className="p-2 bg-blue-50 border border-blue-200 rounded space-y-1">
            <p className="text-[10px] text-blue-800" title={GUIDANCE_SIGNATURE_LINE}>
              {GUIDANCE_SIGNATURE_LINE}
            </p>
            <p className="text-[10px] text-blue-700">{GUIDANCE_SIGNATURE_LINE_WHY}</p>
          </div>
        )}


        {/* PUSH BUTTON — one input for the ACTION. The button's NAME is the Field
            Label above, and for a push button that single string is both the words
            printed on the control (/MK /CA) and the name a screen reader announces.
            It is the only control in PDF whose name does not come from /TU. */}
        {field.type === 'push_button' && (
          <div className="p-2 bg-blue-50 border border-blue-200 rounded space-y-2">
            <p className="text-[10px] text-blue-800">{GUIDANCE_PUSH_BUTTON}</p>
            <div>
              <label className={labelCls} title={GUIDANCE_PUSH_BUTTON_ACTION}>
                When pressed <GuidanceMark />
              </label>
              <select
                value={field.pushButtonConfig?.action || 'reset'}
                title={GUIDANCE_PUSH_BUTTON_ACTION}
                onChange={(e) => updateField('pushButtonConfig', {
                  ...field.pushButtonConfig,
                  action: e.target.value as 'reset' | 'url',
                })}
                className={inputCls}
              >
                <option value="reset">Clear the form</option>
                <option value="url">Open a link</option>
              </select>
            </div>
            {field.pushButtonConfig?.action === 'url' && (
              <div>
                <label className={labelCls}>Link address</label>
                <input
                  type="url"
                  value={field.pushButtonConfig?.url || ''}
                  onChange={(e) => updateField('pushButtonConfig', {
                    ...field.pushButtonConfig,
                    action: 'url',
                    url: e.target.value,
                  })}
                  className={inputCls}
                  placeholder="https://example.gov/guidance"
                />
              </div>
            )}
            <p className="text-[10px] text-blue-700">{GUIDANCE_PUSH_BUTTON_ACTION}</p>
          </div>
        )}

        {/* THE APPEARANCE PAIR — Size and Text Alignment, side by side only
            when the PANEL is wide enough for both. This used to key on the
            viewport (`sm:grid-cols-2`), which carried the same inversion the
            main grid had: a wide window is exactly when this panel is a
            ~340px rail, and two cells of ~150px cannot hold a three-button
            alignment group without wrapping. Same fact, same source: the
            parent owns the splitter, so the parent says how wide this panel
            really is through `twoColumn`. In a rail the pair stacks; height
            is no longer the scarce dimension here, because the rail runs the
            full workspace height and scrolls. */}
        <div className={`grid ${twoColumn ? 'grid-cols-2' : 'grid-cols-1'} gap-3 items-start`}>
          {/* Font SIZE only (all field types — for checkboxes this sizes the check mark).
              Empty = auto: size from field height.

              There was a Font Family select beside this until 2026-09-12. It offered
              Helvetica / Times / Courier, all Standard-14 and therefore UNEMBEDDED, so
              any field using one produced a PDF that fails PDF/UA-1 clause 7.21.4.1.
              Every document is now drawn in one embedded family (Atkinson Hyperlegible),
              chosen for character disambiguation — Il1 and O0 — which is what reference
              and case numbers are made of. Re-adding a picker re-breaks the clause. */}
          <div className="flex items-end gap-2">
            <div className="w-20">
              <label className={labelCls}>Size (pt)</label>
              <input
                type="number"
                min={4}
                max={72}
                value={field.fontSize ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    updateField('fontSize', undefined);
                  } else {
                    const num = parseInt(value);
                    if (!isNaN(num) && num > 0) {
                      updateField('fontSize', num);
                    }
                  }
                }}
                className={inputCls}
                placeholder={`Auto (${
                  field.type === 'checkbox'
                    ? Math.round(field.height * 0.7)
                    : autoFontSize(field.height, field.type === 'textarea')
                })`}
                title={field.type === 'checkbox'
                  ? 'Check-mark size in points. Empty = auto-scaled to the box.'
                  : 'Font size in points. Empty = auto-sized from the field height.'}
              />
            </div>
          </div>

          {/* Text alignment (PDF quadding) — text-rendering types only. Left is
              the default and stores undefined so it drops out of payloads. */}
          {['text', 'email', 'number', 'date', 'textarea', 'select'].includes(field.type) && (
            <div>
              <label className={labelCls}>Text Alignment</label>
              <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                {([
                  { value: undefined, label: 'Left' },
                  { value: 'center' as FieldTextAlign, label: 'Center' },
                  { value: 'right' as FieldTextAlign, label: 'Right' },
                ]).map(opt => {
                  const active = (field.textAlign ?? undefined) === opt.value
                    || (opt.value === undefined && !field.textAlign);
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => updateField('textAlign', opt.value)}
                      title={`Align field text ${opt.label.toLowerCase()} (applies to PDFs and preview)`}
                      className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        active ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Character block configuration */}
        {field.type === 'character_block' && (
          <div className="bg-blue-50 p-2 rounded-lg border border-blue-200 space-y-2">
            <div className="text-xs font-semibold text-blue-900">Character Block Settings</div>
            <p className="text-[10px] text-blue-800">
              Individual character boxes. Use the presets, or set gaps manually (e.g. SSN 3-2-4).
            </p>
            {/* Sits above the Blocks input rather than inside its half-width grid
                cell, where a sentence this long is unreadable. The Blocks control
                itself carries the same text as a tooltip.

                Once the block is split the count stops being the whole story - each
                group becomes its own field with its own name - so the group sentence
                replaces it rather than joining it. */}
            <p className="text-[10px] text-blue-800">
              {charBlockGroups.length > 1 ? GUIDANCE_CHARACTER_BLOCK_GROUPS : GUIDANCE_CHARACTER_BLOCK}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls} title={GUIDANCE_CHARACTER_BLOCK}>
                  Blocks <GuidanceMark />
                </label>
                <input
                  type="number"
                  title={GUIDANCE_CHARACTER_BLOCK}
                  value={field.characterBlockConfig?.totalBlocks || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateCharBlock({ totalBlocks: '' });
                    } else {
                      const totalBlocks = parseInt(value);
                      if (!isNaN(totalBlocks) && totalBlocks >= 1) {
                        updateCharBlock({
                          totalBlocks,
                          // An unsplit block is one group spanning every box, so it follows
                          // the count. A split one does not: its groups are the author's, and
                          // a disagreement shows up in the red boxes tally for them to settle.
                          groups: charBlockGroups.length === 1
                            ? [{ boxes: totalBlocks }]
                            : field.characterBlockConfig?.groups,
                          // NOTHING ELSE IS RESET HERE. Changing the box count used to
                          // write back blockSpacing, groupSeparator, maxCharsPerBlock and
                          // validationPattern as DEFAULTS - so an author who had set the
                          // spacing to match the printed boxes lost it by typing a new
                          // count. None of those four follow from the count. Only a PRESET
                          // may replace settings wholesale, because that is what choosing
                          // a preset means. Scott, 2026-09-18.
                          blockWidth: field.characterBlockConfig?.blockWidth || DEFAULT_BLOCK_WIDTH,
                        });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseInt(e.target.value)) || parseInt(e.target.value) < 1) {
                      updateCharBlock({
                        totalBlocks: DEFAULT_TOTAL_BLOCKS,
                        groups: charBlockGroups.length === 1
                          ? [{ boxes: DEFAULT_TOTAL_BLOCKS }]
                          : field.characterBlockConfig?.groups,
                      });
                    }
                  }}
                  min="1"
                  max="20"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Block Width</label>
                <input
                  type="number"
                  value={field.characterBlockConfig?.blockWidth || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateCharBlock({ blockWidth: '' });
                    } else {
                      const blockWidth = parseInt(value);
                      if (!isNaN(blockWidth) && blockWidth >= 10) {
                        updateCharBlock({ blockWidth });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseInt(e.target.value)) || parseInt(e.target.value) < 10) {
                      updateCharBlock({ blockWidth: DEFAULT_BLOCK_WIDTH });
                    }
                  }}
                  min="10"
                  max="100"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Spacing (px)</label>
                {/*
                  ?? NOT ||, AND THE DIFFERENCE WAS A BUG. `0 || ''` is '', so an authored
                  spacing of 0 rendered this input EMPTY while 0 sat correctly in state — and
                  onBlur below then read that empty input and "repaired" it to the default,
                  discarding the 0. min="0" says 0 was always meant to be legal, and it is:
                  adjacent box borders supply their own separation when the boxes touch.
                  Showing DEFAULT_BLOCK_SPACING when nothing is set keeps the editor honest
                  about the value the backend will use — safeGetInt's default is the same 5.
                */}
                <input
                  type="number"
                  value={field.characterBlockConfig?.blockSpacing ?? DEFAULT_BLOCK_SPACING}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateCharBlock({ blockSpacing: '' });
                    } else {
                      const blockSpacing = parseInt(value);
                      if (!isNaN(blockSpacing) && blockSpacing >= 0) {
                        updateCharBlock({
                          totalBlocks: field.characterBlockConfig?.totalBlocks || DEFAULT_TOTAL_BLOCKS,
                          blockSpacing,
                          groupSeparator: field.characterBlockConfig?.groupSeparator || DEFAULT_GROUP_SEPARATOR,
                          maxCharsPerBlock: field.characterBlockConfig?.maxCharsPerBlock || 1,
                          validationPattern: field.characterBlockConfig?.validationPattern || DEFAULT_VALIDATION_PATTERN,
                        });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || isNaN(parseInt(e.target.value)) || parseInt(e.target.value) < 0) {
                      updateCharBlock({ blockSpacing: DEFAULT_BLOCK_SPACING });
                    }
                  }}
                  min="0"
                  max="50"
                  className={inputCls}
                />
              </div>
            </div>
            {/* Groups - one accessible name per run of boxes.

                One group shows no name column at all: the Field Label above already
                names it, and asking for the name twice is the kind of friction that
                gets worked around rather than complied with. Adding a second group is
                what makes the names appear and become required. */}
            <div className="border-t border-blue-200 pt-2 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <label className={`${labelCls} mb-0`} title={GUIDANCE_CHARACTER_BLOCK_GROUPS}>
                  Groups <GuidanceMark />
                </label>
                {/* A preset is an explicit choice, never a pre-fill: an auto-filled name
                    looks finished, and a name that looks finished is exactly what ships
                    the failure this feature exists to prevent. So the picker always reads
                    "Apply a preset" and never claims one is already in force. */}
                <select
                  value=""
                  title={GUIDANCE_CHARACTER_BLOCK_GROUPS}
                  aria-label="Apply a group preset"
                  onChange={(e) => applyCharBlockGroupPreset(e.target.value)}
                  className="text-[10px] px-1 py-0.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Apply a preset…</option>
                  {CHARACTER_BLOCK_GROUP_PRESETS.map(preset => (
                    <option key={preset.id} value={preset.id}>{preset.name}</option>
                  ))}
                </select>
              </div>

              {charBlockGroups.map((group, index) => {
                const unnamed = charBlockGroups.length > 1 && (group.label || '').trim() === '';
                return (
                  <div key={index} className="flex items-center gap-1">
                    <input
                      type="number"
                      aria-label={`Boxes in group ${index + 1}`}
                      value={group.boxes || ''}
                      onChange={(e) => {
                        const boxes = parseInt(e.target.value);
                        setCharBlockGroup(index, { boxes: isNaN(boxes) || boxes < 0 ? 0 : boxes });
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '' || isNaN(parseInt(e.target.value)) || parseInt(e.target.value) < 1) {
                          setCharBlockGroup(index, { boxes: 1 });
                        }
                      }}
                      min="1"
                      max="20"
                      className="w-12 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    {charBlockGroups.length > 1 && (
                      <input
                        type="text"
                        aria-label={`Name for group ${index + 1}`}
                        title={GUIDANCE_CHARACTER_BLOCK_GROUPS}
                        value={group.label || ''}
                        onChange={(e) => setCharBlockGroup(index, { label: e.target.value })}
                        placeholder="Name this group"
                        className={`flex-1 min-w-0 px-2 py-1.5 text-xs border rounded-lg focus:ring-2 ${
                          unnamed
                            ? 'border-orange-400 focus:ring-orange-400 bg-orange-50'
                            : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                        }`}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeCharBlockGroup(index)}
                      disabled={charBlockGroups.length === 1}
                      title="Remove this group"
                      aria-label={`Remove group ${index + 1}`}
                      className="px-1 text-sm text-gray-500 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                    >
                      &#8854;
                    </button>
                  </div>
                );
              })}

              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={addCharBlockGroup}
                  className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] hover:bg-blue-200 transition-colors"
                >
                  &#8853; Add group
                </button>
                {/* The running tally is the sum-equals-total invariant, live. It blocks
                    generation when it disagrees, so it says so here rather than at save. */}
                <span
                  aria-live="polite"
                  className={`text-[10px] ${charBlockBoxesMatch ? 'text-gray-600' : 'text-red-600 font-semibold'}`}
                >
                  {charBlockBoxes} of {charBlockTotal || 0} boxes{charBlockBoxesMatch ? ' ✓' : ''}
                </span>
              </div>

              {/* A countdown, not a wall: the same shape as the field-list count, so an
                  unfinished block reads as work remaining rather than an error. */}
              {charBlockGroups.length > 1 && (
                <div
                  aria-live="polite"
                  className={`text-[10px] ${
                    charBlockNamed === charBlockGroups.length ? 'text-gray-600' : 'text-orange-600'
                  }`}
                >
                  {charBlockNamed} of {charBlockGroups.length} groups named
                </div>
              )}

              {charBlockWarnings.map((warning, index) => (
                <p key={index} className="text-[10px] text-amber-700">{warning}</p>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>First Gap (px)</label>
                <input
                  type="number"
                  value={field.characterBlockConfig?.firstGap ?? ''}
                  onChange={(e) => updateCharBlock({ firstGap: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 })}
                  min="0"
                  max="150"
                  placeholder="0"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>After Position</label>
                <input
                  type="number"
                  value={field.characterBlockConfig?.firstGapPosition !== undefined ? field.characterBlockConfig.firstGapPosition + 1 : ''}
                  onChange={(e) => updateCharBlock({ firstGapPosition: e.target.value === '' ? undefined : (parseInt(e.target.value) - 1) || 0 })}
                  min="1"
                  max="20"
                  placeholder="Position"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Second Gap (px)</label>
                <input
                  type="number"
                  value={field.characterBlockConfig?.secondGap ?? ''}
                  onChange={(e) => updateCharBlock({ secondGap: e.target.value === '' ? 0 : parseInt(e.target.value) || 0 })}
                  min="0"
                  max="150"
                  placeholder="0"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>After Position</label>
                <input
                  type="number"
                  value={field.characterBlockConfig?.secondGapPosition !== undefined ? field.characterBlockConfig.secondGapPosition + 1 : ''}
                  onChange={(e) => updateCharBlock({ secondGapPosition: e.target.value === '' ? undefined : (parseInt(e.target.value) - 1) || 0 })}
                  min="1"
                  max="20"
                  placeholder="Position"
                  className={inputCls}
                />
              </div>
            </div>
        {/* DEPRECATED 2026-09-12 — Quick Presets hidden, not deleted.
            These were LAYOUT presets: they drew gaps and named nothing. The Groups
            editor above now sets boxes AND accessible names together, so SSN and Phone
            existed twice with different meanings — one of which produced silently
            unnamed comb fields. Hidden rather than removed so the layout arithmetic in
            applyCharBlockPreset (which derives blockWidth to span the drawn rectangle)
            survives for reference until the Groups editor is proven on a real form.
            Delete this block, and applyCharBlockPreset with it, once that is settled. */}
        {false && (
            <div>
              <label className={labelCls}>Quick Presets</label>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() =>
                    applyCharBlockPreset({
                      totalBlocks: 9, blockSpacing: 5,
                      firstGap: 10, firstGapPosition: 2, secondGap: 10, secondGapPosition: 4,
                      maxCharsPerBlock: 1, validationPattern: '[0-9]',
                    })
                  }
                  className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] hover:bg-blue-200 transition-colors"
                >
                  SSN (3-2-4)
                </button>
                <button
                  type="button"
                  onClick={() =>
                    applyCharBlockPreset({
                      totalBlocks: 10, blockSpacing: 5,
                      firstGap: 10, firstGapPosition: 2, secondGap: 10, secondGapPosition: 5,
                      maxCharsPerBlock: 1, validationPattern: '[0-9]',
                    })
                  }
                  className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-[10px] hover:bg-green-200 transition-colors"
                >
                  Phone (3-3-4)
                </button>
                <button
                  type="button"
                  onClick={() =>
                    applyCharBlockPreset({
                      totalBlocks: 6, blockSpacing: 3,
                      firstGap: 0, secondGap: 0,
                      maxCharsPerBlock: 1, validationPattern: '[A-Z0-9]',
                    })
                  }
                  className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-[10px] hover:bg-purple-200 transition-colors"
                >
                  Code (6 chars)
                </button>
              </div>
            </div>
        )}
            <div className="p-2 bg-gray-50 rounded text-[10px] text-gray-600 space-y-0.5">
              <div>📏 {field.characterBlockConfig?.totalBlocks || DEFAULT_TOTAL_BLOCKS} character blocks</div>
              <div>📐 Spacing: {field.characterBlockConfig?.blockSpacing ?? DEFAULT_BLOCK_SPACING}px between blocks</div>
              <div>🔲 First gap: {field.characterBlockConfig?.firstGap || 0}px after position {field.characterBlockConfig?.firstGapPosition !== undefined ? field.characterBlockConfig.firstGapPosition + 1 : 'none'}</div>
              <div>🔳 Second gap: {field.characterBlockConfig?.secondGap || 0}px after position {field.characterBlockConfig?.secondGapPosition !== undefined ? field.characterBlockConfig.secondGapPosition + 1 : 'none'}</div>
            </div>
          </div>
        )}

        {/* Radio group configuration */}
        {field.type === 'radio_group' && (
          <div className="bg-purple-50 p-2 rounded-lg border border-purple-200 space-y-2">
            <div className="text-xs font-semibold text-purple-900">Radio Button Settings</div>
            <p className="text-[10px] text-purple-800">
              The field area contains all radio buttons — set each circle's position below.
            </p>
            <div>
              <label className={labelCls}>Layout</label>
              <select
                value={field.radioGroupConfig?.layout || 'vertical'}
                onChange={(e) =>
                  updateField('radioGroupConfig', {
                    ...field.radioGroupConfig,
                    options: field.radioGroupConfig?.options || [
                      { value: 'option1', label: 'Option 1' },
                      { value: 'option2', label: 'Option 2' },
                    ],
                    layout: e.target.value,
                    required: field.radioGroupConfig?.required || false,
                  })
                }
                className={inputCls}
              >
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Options</label>
              <div className="space-y-2">
                {(field.radioGroupConfig?.options || []).map((option, index) => (
                  <div key={index} className="bg-white p-2 rounded border border-gray-200 space-y-1">
                    <div className="grid grid-cols-2 gap-1">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Value</label>
                        <input
                          type="text"
                          value={option.value}
                          onChange={(e) => {
                            const updatedOptions = [...(field.radioGroupConfig?.options || [])];
                            updatedOptions[index] = { ...updatedOptions[index], value: e.target.value };
                            updateRadioOptions(updatedOptions);
                          }}
                          className="w-full px-1.5 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label
                          className="block text-[10px] font-medium text-gray-600 mb-0.5"
                          title={GUIDANCE_RADIO_OPTION_LABEL}
                        >
                          Label <GuidanceMark />
                        </label>
                        <input
                          type="text"
                          value={option.label}
                          title={GUIDANCE_RADIO_OPTION_LABEL}
                          onChange={(e) => {
                            const updatedOptions = [...(field.radioGroupConfig?.options || [])];
                            updatedOptions[index] = { ...updatedOptions[index], label: e.target.value };
                            updateRadioOptions(updatedOptions);
                          }}
                          className={`w-full px-1.5 py-1 text-[11px] border rounded focus:ring-1 focus:ring-purple-500 ${
                            radioOptionLabelMissing(index) ? 'border-red-400 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {radioOptionLabelMissing(index) && (
                          <p className="text-[9px] text-red-600 mt-0.5">
                            Name this option — two unnamed options are announced identically.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      {([['x', 'X', 0], ['y', 'Y', 0], ['width', 'W', 12], ['height', 'H', 12]] as const).map(([key, label, fallback]) => (
                        <div key={key}>
                          <label className="block text-[10px] font-medium text-gray-600 mb-0.5">{label}</label>
                          <input
                            type="number"
                            value={(option as any)[key] || ''}
                            onChange={(e) => {
                              const updatedOptions = [...(field.radioGroupConfig?.options || [])];
                              updatedOptions[index] = { ...updatedOptions[index], [key]: parseInt(e.target.value) || fallback };
                              updateRadioOptions(updatedOptions);
                            }}
                            className="w-full px-1.5 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                            placeholder={String(fallback)}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-1 text-[10px] text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={option.selected || false}
                          onChange={(e) => {
                            const updatedOptions = [...(field.radioGroupConfig?.options || [])];
                            if (e.target.checked) {
                              // Radio semantics: only one selected at a time
                              updatedOptions.forEach((opt, i) => {
                                opt.selected = i === index;
                              });
                            } else {
                              updatedOptions[index] = { ...updatedOptions[index], selected: false };
                            }
                            updateRadioOptions(updatedOptions);
                          }}
                          className="w-3 h-3 text-purple-600 focus:ring-purple-500"
                        />
                        Selected by default
                      </label>
                      <button
                        onClick={() => updateRadioOptions((field.radioGroupConfig?.options || []).filter((_, i) => i !== index))}
                        className="px-1.5 py-0.5 bg-red-100 text-red-800 rounded text-[10px] hover:bg-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  const currentOptions = field.radioGroupConfig?.options || [];
                  const newOption = {
                    value: `option${currentOptions.length + 1}`,
                    label: `Option ${currentOptions.length + 1}`,
                    x: 0,
                    y: currentOptions.length * 25,
                    width: 12,
                    height: 12,
                  };
                  updateField('radioGroupConfig', {
                    ...field.radioGroupConfig,
                    groupName: field.radioGroupConfig?.groupName || `radio_group_${Date.now()}`,
                    layout: field.radioGroupConfig?.layout || 'vertical',
                    required: field.radioGroupConfig?.required || false,
                    options: [...currentOptions, newOption],
                  });
                }}
                className="mt-1 px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-[10px] hover:bg-purple-200"
              >
                + Add Option
              </button>
            </div>
          </div>
        )}

        {/* Select/dropdown configuration */}
        {field.type === 'select' && (
          <div className="bg-green-50 p-2 rounded-lg border border-green-200 space-y-2">
            <div className="text-xs font-semibold text-green-900">Dropdown Settings</div>
            <p className="text-[10px] text-green-800">
              Configure the options users pick from; mark one as the default.
            </p>
            <div>
              <label className={labelCls}>Options</label>
              <div className="space-y-2">
                {(field.selectConfig?.options || []).map((option, index) => (
                  <div key={index} className="bg-white p-2 rounded border border-gray-200 space-y-1">
                    <div className="grid grid-cols-2 gap-1">
                      <div>
                        <label className="block text-[10px] text-gray-600 mb-0.5">Value</label>
                        <input
                          type="text"
                          value={option.value}
                          onChange={(e) => {
                            const newOptions = [...(field.selectConfig?.options || [])];
                            newOptions[index] = { ...newOptions[index], value: e.target.value };
                            updateSelectOptions(newOptions);
                          }}
                          className="w-full px-1.5 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-green-500"
                          placeholder="option_value"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-600 mb-0.5">Label</label>
                        <input
                          type="text"
                          value={option.label}
                          onChange={(e) => {
                            const newOptions = [...(field.selectConfig?.options || [])];
                            newOptions[index] = { ...newOptions[index], label: e.target.value };
                            updateSelectOptions(newOptions);
                          }}
                          className="w-full px-1.5 py-1 text-[11px] border border-gray-300 rounded focus:ring-1 focus:ring-green-500"
                          placeholder="Display Text"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-1 text-[10px] text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={option.selected || false}
                          onChange={(e) => {
                            const newOptions = [...(field.selectConfig?.options || [])];
                            if (e.target.checked && !field.selectConfig?.allowMultiple) {
                              newOptions.forEach((opt, i) => {
                                opt.selected = i === index;
                              });
                            } else {
                              newOptions[index] = { ...newOptions[index], selected: e.target.checked };
                            }
                            updateSelectOptions(newOptions);
                          }}
                          className="w-3 h-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        Selected by default
                      </label>
                      <button
                        onClick={() => {
                          if ((field.selectConfig?.options || []).length <= 1) return;
                          updateSelectOptions((field.selectConfig?.options || []).filter((_, i) => i !== index));
                        }}
                        className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] hover:bg-red-200"
                        disabled={(field.selectConfig?.options || []).length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  const currentOptions = field.selectConfig?.options || [];
                  updateSelectOptions([
                    ...currentOptions,
                    { value: `option${currentOptions.length + 1}`, label: `Option ${currentOptions.length + 1}`, selected: false },
                  ]);
                }}
                className="mt-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-[10px] hover:bg-green-200"
              >
                + Add Option
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Footer: delete only — edits are live, Save Session persists */}
      <div className="px-3 py-2 border-t border-gray-200">
        <button
          onClick={onDelete}
          className="w-full px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Delete Field
        </button>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">
          Changes apply instantly to the working form — click Save Session / Update Session to keep them (required before generating a project).
        </p>
      </div>
    </div>
  );
};

export default FieldPropertiesSidebar;
